import React, { useState } from 'react';
import Nav from "../Nav";
import Header from "./Header Individual";
import FeatureHeadline from './FeatureHeadline';
import ContactForm from './Contact';

const IndividualTutoringShowcase = () => {
    // State to track whether we're highlighting the contact form
    const [highlight, setHighlight] = useState(false);

    // This function scrolls to the contact form and triggers highlight styling
    const scrollToContactForm = () => {
        const contactFormSection = document.getElementById("contact-form-section");
        if (contactFormSection) {
            contactFormSection.scrollIntoView({ behavior: "smooth" });
            setHighlight(true);
            // Remove highlight after 2 seconds
            setTimeout(() => {
                setHighlight(false);
            }, 2000);
        }
    };

    return (
        <>
            <Nav />
            <Header />
            <FeatureHeadline />

            {/* Introduction Section */}
            <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
                <header className="max-w-4xl mx-auto text-center mb-12">
                    <h1 className="text-5xl font-extrabold text-gray-800 mb-4 leading-tight">
                        Individual Practice
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed">
                        Experience a data-driven, personalized approach to learning—built just for you.
                    </p>
                </header>

                {/* Card Section */}
                <div className="max-w-5xl mx-auto space-y-12">
                    <div className="bg-white shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl p-6 md:grid md:grid-cols-3 md:gap-8">
                        <div className="md:col-span-1">
                            <img
                                className="w-full h-auto object-cover rounded-xl"
                                src={require("./corinne-kutz-tMI2_-r5Nfo-unsplash.jpg")}
                                alt="Student studying with a laptop and notes"
                            />
                        </div>
                        <div className="md:col-span-2 mt-6 md:mt-0">
                            <h2 className="text-3xl font-bold text-gray-800 leading-tight">Tailored Learning Plans</h2>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                Our system analyzes your current skills, goals, and pace
                                to create a custom roadmap for improvement. No two learners
                                are the same, so we adapt lessons to challenge you
                                just the right amount.
                            </p>
                            <ul className="mt-5 list-disc list-inside text-gray-600 space-y-2">
                                <li>AI-driven diagnostics for pinpointing strengths & weaknesses</li>
                                <li>Adaptive practice sets and real-time feedback</li>
                                <li>Detailed progress tracking and analytics</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-white shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl p-6 md:grid md:grid-cols-2 md:gap-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800 leading-tight">Stay on Track</h3>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                                Never miss another assignment. Our system sends emails about new tasks,
                                upcoming deadlines, and personal progress updates—so you can focus on learning,
                                not logistics.
                            </p>
                        </div>
                        <div className="mt-6 md:mt-0">
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Timely notifications before due dates</li>
                                <li>Instant feedback on completed work</li>
                                <li>Weekly summaries for consistent improvement</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
                <header className="max-w-4xl mx-auto text-center mb-12">
                    <h2 className="text-4xl font-extrabold text-gray-800 mb-4">
                        Pricing
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        Choose a plan that fits your needs.
                    </p>
                </header>

                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Month-to-month option */}
                    <div className="border border-gray-200 rounded-lg p-8 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Month-to-month</h3>
                        <p className="text-4xl font-extrabold my-4 text-gray-800">
                            $49
                            <span className="text-base font-medium text-gray-600"> / month</span>
                        </p>
                        <ul className="space-y-2 text-gray-600 leading-relaxed">
                            <li>Flexible subscription, cancel anytime</li>
                            <li>Adaptive lessons & analytics</li>
                            <li>Email and chat support</li>
                        </ul>
                        <button
                            className="mt-6 w-full bg-indigo-600 text-white font-semibold py-2 px-4 rounded transition-colors duration-200 hover:bg-indigo-700"
                            onClick={scrollToContactForm}
                        >
                            Start Monthly
                        </button>
                    </div>

                    {/* Yearly option */}
                    <div className="border border-gray-200 rounded-lg p-8 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Yearly</h3>
                        <p className="text-4xl font-extrabold my-4 text-gray-800">
                            $499
                            <span className="text-base font-medium text-gray-600"> / year</span>
                        </p>
                        <ul className="space-y-2 text-gray-600 leading-relaxed">
                            <li>Save nearly 15% vs. monthly</li>
                            <li>Includes everything in monthly</li>
                            <li>Priority email and chat support</li>
                        </ul>
                        <button
                            className="mt-6 w-full bg-indigo-600 text-white font-semibold py-2 px-4 rounded transition-colors duration-200 hover:bg-indigo-700"
                            onClick={scrollToContactForm}
                        >
                            Start Yearly
                        </button>
                    </div>
                </div>
            </section>

            {/* Contact Section: include the highlight ring conditionally */}
            <div
                id="contact-form-section"
                className={`bg-white transition-shadow duration-300 ${
                    highlight ? "ring-4 ring-indigo-400" : ""
                }`}
            >
                <ContactForm />
            </div>
        </>
    );
};

export default IndividualTutoringShowcase;

